import { useMemo } from 'react';
import {
  buildPromotionCustomColorsFullFromTokens,
  buildPromotionCustomColorsLiteFromTokens,
  isPromotionDarkSurfaceFromPresetBgAndFullColors,
  type PromotionCustomColorsFull,
  type PromotionCustomColorsLite,
  type PromotionModuleVisualMask,
  type PromotionModuleVisualTokens,
} from '@utils/promotionContentVisual';

interface UsePromotionModuleCustomColorsParams {
  bgType?: string;
  visualTokens: PromotionModuleVisualTokens;
  mask: PromotionModuleVisualMask;
  pointColorFallback?: string;
}

interface UsePromotionModuleCustomColorsResult {
  presetBgType: string;
  isCustom: boolean;
  customColorsFull: PromotionCustomColorsFull | null;
  customColorsLite: PromotionCustomColorsLite | null;
  isDarkSurface: boolean;
  productUnitColorType: 'white' | undefined;
  productSkeletonBgType: string;
}

export function usePromotionModuleCustomColors({
  bgType,
  visualTokens,
  mask,
  pointColorFallback,
}: UsePromotionModuleCustomColorsParams): UsePromotionModuleCustomColorsResult {
  const normalizedBgType = bgType?.toLowerCase();
  const {
    colorType,
    backgroundColorHex,
    titleTone,
    pointColorHex,
    textEmphasisTone,
  } = visualTokens;

  const presetBgType = useMemo(
    () =>
      normalizedBgType && normalizedBgType !== 'custom'
        ? normalizedBgType
        : 'white',
    [normalizedBgType]
  );

  const isCustom = colorType === 'CUSTOM';

  const customColorsFull = useMemo(() => {
    if (!isCustom || mask !== 'full') return null;
    return buildPromotionCustomColorsFullFromTokens(
      {
        colorType,
        backgroundColorHex,
        titleTone,
        pointColorHex,
        textEmphasisTone,
      },
      {
        pointColorFallback,
      }
    );
  }, [
    isCustom,
    mask,
    colorType,
    backgroundColorHex,
    titleTone,
    pointColorHex,
    textEmphasisTone,
    pointColorFallback,
  ]);

  const customColorsLite = useMemo(() => {
    if (!isCustom || mask !== 'twoField') return null;
    return buildPromotionCustomColorsLiteFromTokens({
      colorType,
      backgroundColorHex,
      titleTone,
    });
  }, [isCustom, mask, colorType, backgroundColorHex, titleTone]);

  const isDarkSurface = useMemo(() => {
    if (mask === 'full') {
      return isPromotionDarkSurfaceFromPresetBgAndFullColors(
        presetBgType,
        customColorsFull
      );
    }

    return customColorsLite?.textColor.trim().toLowerCase() === '#ffffff';
  }, [mask, presetBgType, customColorsFull, customColorsLite]);

  const productUnitColorType =
    normalizedBgType === 'black' ? 'white' : undefined;
  const productSkeletonBgType = normalizedBgType || 'white';

  return {
    presetBgType,
    isCustom,
    customColorsFull,
    customColorsLite,
    isDarkSurface,
    productUnitColorType,
    productSkeletonBgType,
  };
}
